import React, { useState, useMemo, useCallback, useRef } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import styles from './MediaView.module.scss'
import {
  GET_MEDIA_LIBRARY_QUERY,
  GET_PROJECTS_QUERY,
  ATTACH_MEDIA_ASSETS_MUTATION,
} from '../query/gqlQueries'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import LinkIcon from '@mui/icons-material/Link'
import VideocamIcon from '@mui/icons-material/Videocam'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import FileUploadIcon from '@mui/icons-material/FileUpload'

// Rewrite the server-baked thumbnail origin to the client's image host — same
// helper as MediaView. Uploads POST directly to the image-server on the mini,
// so which machine the browser runs on is irrelevant — only this URL matters.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI
const STORE_TOKEN = process.env.REACT_APP_IMAGE_STORE_TOKEN || ''

function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function thumbSrc(url) {
  if (!url) return url
  if (!IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch { return url }
}

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

// "2026-07-17" → "17 Jul 2026"
function prettyDate(d) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return d
  const [y, m, day] = d.split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function MediaLibrary({ username, onBack, setIsVisible }) {
  const { data, loading, refetch } = useQuery(GET_MEDIA_LIBRARY_QUERY, {
    variables: { username },
    skip: !username,
    fetchPolicy: 'cache-and-network',
  })
  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, { variables: { username }, skip: !username })
  const [attachAssets, { loading: attaching }] = useMutation(ATTACH_MEDIA_ASSETS_MUTATION)

  const days = useMemo(() => data?.getMediaLibrary || [], [data])
  const existingProjects = projectsData?.getProjects || []

  const [selected, setSelected] = useState(new Set())
  const [attachProject, setAttachProject] = useState('')

  // Import panel: upload files from the browser (SD card, laptop, anywhere)
  // straight into _library/<date>/<device>/ on the mini.
  const [importOpen, setImportOpen] = useState(false)
  const [importDate, setImportDate] = useState(todayISO())
  const [importDevice, setImportDevice] = useState('')
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState(null)
  const importInputRef = useRef(null)

  const handleImport = useCallback(async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    const date = (importDate || '').trim()
    const device = (importDevice || '').trim()
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { window.alert('Pick a valid date (YYYY-MM-DD).'); return }
    if (!device) { window.alert('Enter a device label first (e.g. Sony, iPhone, DJI).'); return }
    if (!IMAGE_STORE_URI) { window.alert('REACT_APP_IMAGE_STORE_URI is not set — cannot reach the media server.'); return }

    const rel = `_library/${date}/${device}`
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    setImporting(true)
    setImportMsg(`Uploading ${files.length} file${files.length > 1 ? 's' : ''}…`)
    try {
      const res = await fetch(
        `${IMAGE_STORE_URI.replace(/\/$/, '')}/media/upload?path=${encodeURIComponent(rel)}`,
        { method: 'POST', headers: STORE_TOKEN ? { Authorization: `Bearer ${STORE_TOKEN}` } : {}, body: form }
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Upload failed (HTTP ${res.status})`)
      }
      const body = await res.json().catch(() => ({}))
      setImportMsg(`Imported ${(body.files || files).length} file(s) → ${rel}`)
      await refetch()
    } catch (e) {
      console.error('Import failed:', e)
      setImportMsg(null)
      window.alert(
        String(e.message).includes('Failed to fetch')
          ? 'Could not reach the media server (is it running / tunnel up? on the LAN, point REACT_APP_IMAGE_STORE_URI at http://192.168.1.215:4001).'
          : (e.message || 'Import failed')
      )
    } finally {
      setImporting(false)
      if (importInputRef.current) importInputRef.current.value = ''
    }
  }, [importDate, importDevice, refetch])

  const toggle = useCallback((path) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path); else next.add(path)
      return next
    })
  }, [])

  const selectDevice = useCallback((assets) => {
    setSelected((prev) => {
      const next = new Set(prev)
      const allSelected = assets.every((a) => next.has(a.path))
      assets.forEach((a) => { allSelected ? next.delete(a.path) : next.add(a.path) })
      return next
    })
  }, [])

  const clearSelection = () => setSelected(new Set())

  const handleAttach = async () => {
    if (selected.size === 0 || !attachProject.trim()) return
    try {
      await attachAssets({
        variables: { paths: Array.from(selected), project: attachProject.trim(), username },
      })
      clearSelection()
      await refetch()
    } catch (err) {
      console.error('Attach failed:', err)
      window.alert(err.message || 'Failed to attach')
    }
  }

  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

      <div className={styles.container} style={{ paddingBottom: selected.size > 0 ? '5rem' : undefined }}>
        <header className={styles.header}>
          <button className={styles.backBtn} onClick={onBack}>
            <ArrowBackIcon fontSize="small" /> Collections
          </button>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Media · originals</div>
            <h1 className={styles.title}>Library</h1>
          </div>
          <div className={styles.toolbar}>
            <button
              className={`${styles.iconBtn} ${importOpen ? styles.primaryBtn : ''}`}
              title="Import files into the library"
              onClick={() => setImportOpen((v) => !v)}
            >
              <FileUploadIcon fontSize="small" /> Import
            </button>
            <button className={styles.iconBtn} title="Rescan" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {importOpen && (
          <div className={styles.importPanel}>
            <div className={styles.importFields}>
              <label className={styles.importField}>
                <span>Date</span>
                <input type="date" value={importDate} onChange={(e) => setImportDate(e.target.value)} />
              </label>
              <label className={styles.importField}>
                <span>Device</span>
                <input
                  type="text"
                  value={importDevice}
                  onChange={(e) => setImportDevice(e.target.value)}
                  placeholder="Sony · iPhone · DJI…"
                  list="library-devices"
                />
                <datalist id="library-devices">
                  {Array.from(new Set(days.flatMap((d) => d.devices.map((v) => v.device)))).map((dv) => (
                    <option key={dv} value={dv} />
                  ))}
                </datalist>
              </label>
              <button
                className={`${styles.iconBtn} ${styles.primaryBtn}`}
                disabled={importing || !importDevice.trim()}
                onClick={() => importInputRef.current && importInputRef.current.click()}
              >
                <FileUploadIcon fontSize="small" /> {importing ? 'Uploading…' : 'Choose files…'}
              </button>
              <input
                ref={importInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleImport(e.target.files)}
              />
            </div>
            <div className={styles.importHint}>
              Uploads to <code>_library/{importDate || '<date>'}/{importDevice.trim() || '<device>'}/</code> on the mini.
              Big card dumps on the LAN are faster over SMB (<code>smb://192.168.1.215</code>) — then just Rescan.
            </div>
            {importMsg && <div className={styles.importMsg}>{importMsg}</div>}
          </div>
        )}

        {loading && days.length === 0 && <div className={styles.status}>Scanning the library…</div>}

        {!loading && days.length === 0 && (
          <div className={styles.emptyState}>
            <p>No originals in the library yet.</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Offload a card into <code>/Volumes/dataStoreA/media/_library/&lt;date&gt;/&lt;device&gt;/</code> on the mini, then Rescan.
            </p>
          </div>
        )}

        {days.map((day) => (
          <div key={day.date} className={styles.libDay}>
            <div className={styles.libDayHeader}>{prettyDate(day.date)}</div>
            {day.devices.map((dev) => (
              <div key={dev.device} className={styles.libDevice}>
                <div className={styles.libDeviceHeader}>
                  <span>{dev.device} · {dev.assets.length}</span>
                  <button className={styles.libSelectAll} onClick={() => selectDevice(dev.assets)}>
                    {dev.assets.every((a) => selected.has(a.path)) ? 'deselect all' : 'select all'}
                  </button>
                </div>
                <div className={styles.assetGrid}>
                  {dev.assets.map((a) => {
                    const isSel = selected.has(a.path)
                    return (
                      <div
                        key={a.path}
                        className={`${styles.assetCard} ${isSel ? styles.assetCardSelected : ''}`}
                        onClick={() => toggle(a.path)}
                      >
                        <div className={styles.assetThumb}>
                          {a.thumbnailUrl ? (
                            <img src={thumbSrc(a.thumbnailUrl)} alt={a.filename} loading="lazy"
                              onError={(e) => { e.currentTarget.style.opacity = 0.2 }} />
                          ) : (
                            <div className={styles.assetThumbPlaceholder}>
                              {a.kind === 'video' ? <VideocamIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                              <span>{a.kind}</span>
                            </div>
                          )}
                          {isSel && <div className={styles.assetCheck}>✓</div>}
                        </div>
                        <div className={styles.assetMeta}>
                          <div className={styles.assetName} title={a.filename}>{a.filename}</div>
                          <div className={styles.assetStats}>{formatBytes(a.size)}</div>
                          {a.projects && a.projects.length > 0 && (
                            <div className={styles.assetProjects}>
                              {a.projects.map((p) => <span key={p} className={styles.assetProjectTag}>{p}</span>)}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {selected.size > 0 && (
        <div className={styles.attachBar}>
          <span className={styles.attachCount}>{selected.size} selected</span>
          <input
            type="text"
            className={styles.attachInput}
            value={attachProject}
            onChange={(e) => setAttachProject(e.target.value)}
            placeholder="Attach to project…"
            list="library-projects"
          />
          <datalist id="library-projects">
            {existingProjects.map((p) => <option key={p.identity} value={p.properties.title} />)}
          </datalist>
          <button
            className={`${styles.iconBtn} ${styles.primaryBtn}`}
            onClick={handleAttach}
            disabled={attaching || !attachProject.trim()}
          >
            <LinkIcon fontSize="small" /> {attaching ? 'Attaching…' : 'Attach'}
          </button>
          <button className={styles.iconBtn} onClick={clearSelection}>Clear</button>
        </div>
      )}
    </div>
  )
}
